import { useMemo } from "react";
import { format, parseISO } from "date-fns";

const DEPT_LABELS = ["MOLD", "FRAME", "FINISH", "FIBERGLASS", "GFRC"];

function colorScale(value, max) {
  if (!max || value <= 0) return "#0F1827";
  const t = Math.min(value / max, 1);
  if (t < 0.5) {
    const k = t / 0.5;
    return `rgb(${Math.round(0)},${Math.round(48 + 164 * k)},${Math.round(90 + 165 * k)})`;
  }
  const k = (t - 0.5) / 0.5;
  return `rgb(${Math.round(245 * k)},${Math.round(212 - 54 * k)},${Math.round(255 - 244 * k)})`;
}

function textColor(value, max) {
  if (!max) return "#94A3B8";
  const t = value / max;
  return t > 0.45 && t < 0.85 ? "#0A0B10" : "#F8FAFC";
}

function fmtWeek(iso) {
  try { return format(parseISO(iso), "MMM d"); } catch { return iso?.slice(5) || ""; }
}

export default function HoursHeatmap({ data }) {
  const max = useMemo(() => {
    if (!data?.cells?.length) return 0;
    return Math.max(...data.cells.map((c) => c.hours));
  }, [data]);

  if (!data?.weeks?.length) return null;

  const cellByKey = {};
  data.cells.forEach((c) => { cellByKey[`${c.dept}|${c.week}`] = c.hours; });

  return (
    <div className="bg-[#12141D] border border-border p-5">
      <h3 className="font-heading font-semibold">Hours Heatmap</h3>
      <p className="text-xs text-muted-foreground mb-5">Department × Week distribution</p>

      <div className="overflow-x-auto">
        <table className="border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th className="w-24 px-2" />
              {data.weeks.map((w) => (
                <th key={w} className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium px-1 pb-2"
                    style={{ width: 56, whiteSpace: "nowrap" }}>
                  {fmtWeek(w)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DEPT_LABELS.map((dept) => (
              <tr key={dept}>
                <td className="w-24 pr-3 text-right">
                  <span className="text-[11px] font-heading font-semibold tracking-wider">{dept}</span>
                </td>
                {data.weeks.map((w) => {
                  const v = cellByKey[`${dept}|${w}`] || 0;
                  return (
                    <td key={w} className="p-0.5">
                      <div
                        data-testid={`heat-${dept.toLowerCase()}-${w}`}
                        title={`${dept} · ${fmtWeek(w)}: ${v} hrs`}
                        className="flex items-center justify-center font-mono tabular-nums"
                        style={{
                          height: 40, width: "100%", minWidth: 48,
                          background: colorScale(v, max),
                          color: textColor(v, max),
                          fontSize: 10,
                        }}
                      >
                        {v >= 100 ? Math.round(v) : v.toFixed(1)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center gap-3 mt-5 pl-24">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Scale</span>
          <div className="h-2 w-48"
               style={{ background: "linear-gradient(to right,#00305A,#00D4FF,#F59E0B)" }} />
          <span className="text-[10px] font-mono text-muted-foreground">0</span>
          <span className="text-[10px] font-mono text-muted-foreground">→</span>
          <span className="text-[10px] font-mono text-muted-foreground">{Math.round(max)} hrs</span>
        </div>
      </div>
    </div>
  );
}
